import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, X, Check } from 'lucide-react';
import { useUpdateFeatures } from '../../hooks/useApartments';
import type { Apartment } from '../../types/apartment';

const AVAILABLE_FEATURES = [
  'WIFI',
  'AIR_CONDITIONING',
  'HEATING',
  'KITCHEN',
  'WASHING_MACHINE',
  'DRYER',
  'TV',
  'PARKING',
  'ELEVATOR',
  'BALCONY',
  'TERRACE',
  'GARDEN',
  'POOL',
  'GYM',
  'PETS_ALLOWED',
  'SMOKING_ALLOWED',
  'WHEELCHAIR_ACCESSIBLE',
  'FAMILY_FRIENDLY',
  'WORK_FRIENDLY',
] as const;

interface FeaturesSectionProps {
  readonly apartment: Apartment;
}

export const FeaturesSection = ({ apartment }: FeaturesSectionProps) => {
  const { t } = useTranslation(['common', 'badges', 'errors', 'forms']);
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState<string[]>(apartment.details.features);
  const updateMutation = useUpdateFeatures();

  const handleEdit = () => {
    setSelected([...apartment.details.features]);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelected([...apartment.details.features]);
  };

  const handleToggle = (feature: string) => {
    setSelected(prev =>
      prev.includes(feature) ? prev.filter(f => f !== feature) : [...prev, feature]
    );
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({ id: apartment.id, data: { features: selected } });
      setIsEditing(false);
    } catch {
      // error shown inline
    }
  };

  const featureLabel = (feature: string) => {
    const key = `badges:feature${feature.charAt(0) + feature.slice(1).toLowerCase().replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())}`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return t(key as any);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{t('forms:features')}</h2>
        {!isEditing && (
          <button
            onClick={handleEdit}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
            aria-label={t('common:edit')}
          >
            <Pencil size={16} />
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {AVAILABLE_FEATURES.map(feature => (
              <label key={feature} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={selected.includes(feature)}
                  onChange={() => handleToggle(feature)}
                  className="rounded"
                />
                <span className="text-sm text-gray-700">{featureLabel(feature)}</span>
              </label>
            ))}
          </div>
          {updateMutation.isError && (
            <p className="text-sm text-red-600">{t('errors:somethingWentWrong')}</p>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="flex items-center gap-2 py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 min-h-[44px]"
            >
              <Check size={16} />
              {updateMutation.isPending ? t('common:saving') : t('common:save')}
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center gap-2 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium min-h-[44px]"
            >
              <X size={16} />
              {t('common:cancel')}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {apartment.details.features.length === 0 ? (
            <p className="text-sm text-gray-500">—</p>
          ) : (
            apartment.details.features.map(feature => (
              <span key={feature} className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                {featureLabel(feature)}
              </span>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default FeaturesSection;
